import {MortgageSimulationDemand, SimulationDemand} from '@/models/Demand';
import {FFBorrower} from '@/models/ExpertState';
import {MarketType} from '@/models/simulation/MarketType';
import {LoanPurposeActivity} from '@/models/simulation/LoanPurposeActivity';
import {LoanPurposeWhat} from '@/models/simulation/LoanPurposeWhat';
import {LoanPurposeHow} from '@/models/simulation/LoanPurposeHow';
import {BankSymbol} from '@/commons/dictionaries/BankSymbol';
import {Utils} from '@/services/utils/BasicUtils';
import EnvironmentService from '@/env/EnvironmentService';
import {ReferenceRateType} from '@/models/simulation/ReferenceRateType';
import {Gender} from '@/models/enums/Gender';
import {FirstOrSubsequentRealEstate} from '@/models/simulation/FirstOrSubsequentRealEstate';
import {HomeStartProgram} from '@/components/calculator/schedule/Schedule';

export interface LoanPurpose {
  activity: LoanPurposeActivity;
  what?: Nullable<LoanPurposeWhat>;
  market: MarketType;
  how: LoanPurposeHow;
}

export interface CreditHistory {
  exists: boolean;
}

export interface FFAverageCosts {
  car: number,
  house: number,
  life: number,
  other: number,
}

export interface FFHouseholdDTO {
  borrowers: FFBorrower[];
  averageCosts: FFAverageCosts;
  numberOfAdults: Nullable<number>;
  numberOfChildren: Nullable<number>;

  [key: string]: any;
}

export class FFLiabilies {
  accountLimit: number = 0;
  alimony: number = 0;
  cardsLimit: number = 0;
  guarantees: number = 0;
  installmentsFX: number = 0;
  installmentsPLN: number = 0;
  loansLiabilitiesSum: number = 0;
  mortgageLiabilitiesSum: number = 0;
}

export class FFHousehold {
  borrowers: FFBorrower[] = [
    new FFBorrower(),
  ];

  averageCosts: FFAverageCosts = {car: 0, house: 0, life: 0, other: 0,};
  numberOfAdults: Nullable<number> = null;
  numberOfChildren: Nullable<number> = null;
  numberOfCars: Nullable<number> = null;
  realEstateSize: Nullable<number> = null;
  willingToDeclareCosts: boolean = true;
  hasLiabilities: boolean = false;
  liabilities: FFLiabilies = new FFLiabilies();

  constructor(dto?: Partial<FFHousehold>) {
    if (dto) {
      Utils.assign(this, dto);
    }
  }
}

export interface Range {
  from: number;
  until: number;
}

export interface TopOffer {
  bankName: string;
  averageFirstPeriodFee: number;
}

export interface Summary {
  offersCount: number;
  availableOffersCount: number;
  initialCostRange: Range;
  firstInstallmentRange: Range;
  lastInstallmentRange: Range;
  maxCreditAmountRange: Range;
  costsSumRange: Range;
  rrsoRange: Range;
  topOffers: TopOffer[];
}

export interface Info {
  id: string;
  bank: BankSymbol;
  bankName: string;
  insurancesDescription?: string;
  logo: string;
  title: string;
  isVariant: boolean;
  description: string;
  earlierPaymentDescription: string;
  percentageForWhicheverExpenseDescription: string;
  inspectionDescription: string;
  mortgageValuationDescription?: string;
  xsellDescription: string;
  xsellInsurances?: XSellKind[];
  insurancesRequiredFromBankForXsell?: XSellKind[];
  voluntaryInsurances?: XSellKind[];
  trancheAccountingDescription: string;
  conversionDescription: string;
  preparatoryFeeDescription?: string;
}

export interface Overview {
  firstInstallment: number;
  lastInstallment: number;
  initialCost: number;
}

export interface InitialCosts {
  provision: Provision;
  realEstateAppraisal: RealEstateAppraisal;
  realEstateInsurance: RealEstateInsurance;
  lifeInsurance: LifeInsurance;
  paymentInsurance: PaymentInsurance;
  insurancePackage: InsurancePackage;
  brokerCommission?: number;
}

export interface BankAccount {
  name: string;
  description: string;
  fee: number;
  isAcquired: boolean;
}

export interface MonthlyFees {
  firstInstallment: number;
  lastInstallment: number;
  bridgingInsurance: BridgingInsurance[];
  lifeInsurance: LifeInsurance[];
  bankAccount: BankAccount;
  realEstateInsurance: RealEstateInsurance[];
  insurancePackage: InsurancePackage[];
  paymentInsurance: PaymentInsurance[];
  lowPersonalShareInsurance: LowPersonalShareInsurance[];
  insurances?: {
    first: InsurancePackage[];
    second?: InsurancePackage[];
  }
}

export interface NonMonthlyFees {
  realEstateInsurance?: RealEstateInsurance[];
  creditCard?: {
    name: string,
    description: string,
    fee: number,
    isAcquired: boolean,
  };
}

export interface Totals {
  provision: number;
  lowPersonalShareInsurance: number;
  bridgingInsurance: number;
  realEstateInsurance: number;
  lifeInsurance: number;
  bankAccount: number;
  creditCard: number;
  realEstateAppraisal: number;
  paymentInsurance: number;
  insurancePackage: number;
  preparatoryFee: number;
  interest: number;
  capital: number;
  costsSum: number;
  totalSum: number;
}

export interface InterestRate {
  sum: number;
  margin: number;
  referenceRate: number;
  referenceRateType: ReferenceRateType;
  marginDescription: string;
}

export interface Period {
  from: number;
  until: number;
  frequency: string;
}

export interface Insurance {
  fee: number;
  period?: Period;
}

export interface RealEstateInsurance extends Insurance {
  calculationStrategy: string;
  coveredPeriodDescription?: string;
}

export interface LifeInsurance extends Insurance {
  calculationStrategy: string;
  coveredPeriodDescription?: string;
}

export interface BridgingInsurance extends Insurance {
  calculationStrategy: string;
  coveredPeriodDescription?: string;
}

export interface PaymentInsurance extends Insurance {
  calculationStrategy: string;
  coveredPeriodDescription?: string;
}

export interface LowPersonalShareInsurance extends Insurance {
  calculationStrategy: string;
  coveredPeriodDescription?: string;
}

export interface InsurancePackage extends Insurance {
  calculationStrategy: string;
  coveredPeriodDescription?: string;
}

export interface RealEstateAppraisal {
  inspectionCost: number;
  collateralAppraisal: number;
  sum: number;
}

export interface InsuranceCovers {
  life: boolean;
  work: boolean;
  accidents: boolean;
}

export interface InsuranceDescription {
  insuranceDefinitionId: string;
  name: string;
  description: string;
  currentVariantId: string;
  variants: Variant[];
  required: boolean;
  originallyRequired: boolean;
  covers?: InsuranceCovers;
}

export interface Payment {
  paymentDescription: string;
  durationDescription: string;
}

export interface Variant {
  id: string;
  name: string;
  description: string;
  payments: Payment[];
}

export interface InsurancePackageDescription extends InsuranceDescription {
  replaces: XSellKind[],
}

export interface Insurances {
  bridgingInsurance: InsuranceDescription;
  realEstateInsurance: InsuranceDescription;
  lifeInsurance: InsuranceDescription;
  lowPersonalShareInsurance: InsuranceDescription;
  paymentInsurance: InsuranceDescription;
  insurancePackage?: InsurancePackageDescription;
  first?: InsuranceDescription;
  second?: InsuranceDescription;
}

export interface AnyPurposeExpense {
  amount: number;
  percentage: number;
}

export interface Features {
  quarterlyInstallments: boolean;
  creditPromise: boolean;
  negotiable: boolean;
  profitableEarlierPayment: boolean;
  accountBalanceLowersDebt: boolean;
  creditHolidays: boolean;
  baliffAllowed: boolean;
  overnightSeparation: boolean;
  seasonalBusiness: boolean;
  familyTransaction: boolean;
  variableInterest: boolean;
  statementsNotRequired: boolean;
  notEKO: boolean;
  noXsell?: boolean;
  forForeigners: boolean;
  realEstateAppraisalByInspection: boolean;
  jobBreakAccepted: boolean;
  maxWaitingTime: number;
  isRkmProgram?: boolean;
  advantages: string[];
  disadvantages: any[];
  isSafeCredit2Percent?: boolean;
  isNewHomeRO?: boolean;
  fixedInterest: boolean;
  isHomeStartProgram?: boolean;
  remoteProcess?: boolean;
}

export interface CreditWorthiness {
  positive: boolean;
  maxCreditAmount: number;
}

export interface AcceptedIncomeIds {
  household: number;
  borrower: number;
}

export interface MainIncome {
  incomeTypeId: number;
  description: string;
  income: number;
}

export interface AcceptedIncome {
  id: AcceptedIncomeIds;
  mainIncome: MainIncome;
  additionalIncome?: MainIncome;
}

export interface BorrowersInfo {
  acceptedIncomes: AcceptedIncome[];
  refusedBorrowers: any[];
  refusedIncomes: any[];
}

export interface FxValues {
  loanAmount: number;
  originalLoanAmountPLN: number;
  firstInstallment: number;
  lastInstallment: number;
  bridgingInsurance: BridgingInsurance;
  lifeInsurance: LifeInsurance;
  interest: number;
  buyRate: number;
  averageNBPRate: number;
}

export interface LoanPeriodRestrictions {
  maxClientAgeAfterLoan: number;
  minLoanPeriod: number;
  maxLoanPeriod: number;
  isLoanPeriodViolated: boolean;
  isLoanPeriodViolatedDueToBorrowerAge: boolean;
}

export interface LoanAmountRestrictions {
  minAmount: number;
  maxAmount: number;
  isLoanAmountViolated: boolean;
}

export interface ClientAgeRestrictions {
  minClientAge: number;
  maxClientAge: number;
  isClientAgeViolated: boolean;
}

export interface MinLtvRestrictions {

}

export interface MinLtv {
  value: number;
  restrictions: MinLtvRestrictions;
}

export interface MaxLtvRestrictions {
  borrowersNumber: { from?: number, until?: number, };
  inhabitancy: string[];
  collateral: string[];
  income: { from?: number };
  firstOrSubsequentRealEstate?: FirstOrSubsequentRealEstate[];
}

export interface MaxLtv {
  value: number;
  restrictions: MaxLtvRestrictions;
}

export interface LtvPrequalification {
  minLtv: MinLtv[];
  maxLtv: MaxLtv[];
}

export interface LtvRestrictions {
  maxLtv: number;
  ltvPrequalification: LtvPrequalification;
  isLtvViolated: boolean;
}

export interface CollateralsRestrictions {
  availableCollaterals: string[];
  isCollateralsViolated: boolean;
}

export interface RequiredIncomeType {
  incomeTypeId: number;
  minPeriod: number;
  accountingPeriodId: number;
  minExperience: number;
  description: string;
}

export interface RequiredIncomeTypes {
  bank: string;
  incomeTypes: RequiredIncomeType[];
}

export interface IncomeRestrictions {
  requiredIncomeTypes: RequiredIncomeTypes;
  isIncomeTypeViolated: boolean;
  isIncomeDurationViolated: boolean;
  requiredProfessionIds: number[];
  isProfessionsViolated: boolean;
}

export interface Restrictions {
  loanPeriodRestrictions: LoanPeriodRestrictions;
  loanAmountRestrictions: LoanAmountRestrictions;
  clientAgeRestrictions: ClientAgeRestrictions;
  ltvRestrictions: LtvRestrictions;
  collateralsRestrictions: CollateralsRestrictions;
  incomeRestrictions: IncomeRestrictions;
  isIndispensableCreditingsViolated: boolean;
  isBorrowersViolated: boolean;
  maxBorrowersNumber: number;
  onlyFixedInstallmentsViolated?: boolean;
  onlyFixedInstallments?: boolean;
}

export enum Cost {
  BRIDGING_INSURANCE = 'BridgingInsurance',
  LIFE_INSURANCE = 'LifeInsurance',
  LOW_PERSONAL_SHARE_INSURANCE = 'LowPersonalShareInsurance',
  PAYMENT_INSURANCE = 'PaymentInsurance',
  REAL_ESTATE_INSURANCE = 'RealEstateInsurance',
  INSURANCE_PACKAGE = 'InsurancePackage',
  PROVISION = 'Provision',
  PREPARATORY_FEE = 'PreparatoryFee',
  FIRST_INSURANCE = 'First',
  SECOND_INSURANCE = 'Second',
}
export type XSellKind = Exclude<Cost, Cost.PROVISION | Cost.PREPARATORY_FEE>;

export type XSellType = Uncapitalize<XSellKind>;

export type MortgageXSellKind = Exclude<XSellKind, Cost.FIRST_INSURANCE | Cost.SECOND_INSURANCE>;

export type MortgageXSellType = Uncapitalize<MortgageXSellKind>;

export interface AdditionalProduct {
  required: boolean;
  name: string;
  fee: number;
  description: string;
  acquired: boolean;
}

export interface AdditionalProducts {
  bankAccount: AdditionalProduct;
}

export interface CreditedInitialCosts {
  bridgingInsurance: BridgingInsurance,
  realEstateInsurance: RealEstateInsurance,
  lifeInsurance: LifeInsurance,
  paymentInsurance: PaymentInsurance,
  lowPersonalShareInsurance: LowPersonalShareInsurance,
  insurancePackage: InsurancePackage,
  provision: Provision,
}

export interface DebtToIncome {
  acceptableDTI: number,
  acceptableDtiPercentage: number,
  householdDTI?: number,
  householdDtiPercentage?: number,
  averageCostSum?: number,
  liabilitiesSum?: number,
  totalCost?: number,
  incomeSum?: number,
  installment?: number,
}

export interface Offer {
  info: Info;
  overview: Overview;
  initialCosts: InitialCosts;
  monthlyFees: MonthlyFees;
  nonMonthlyFees: NonMonthlyFees;
  totals: Totals;
  customizations?: {
    customLoanPeriodInMonths?: number,
    customLoanAmount?: number,
    maxLoanAmount?: number,
  };
  additionalProducts?: AdditionalProducts;
  insurances: Insurances;
  interestRate: InterestRate;
  rrso: number;
  anyPurposeExpense: AnyPurposeExpense;
  currencySpread: number;
  features: Features;
  creditedCosts: Cost[];
  creditWorthiness?: CreditWorthiness;
  borrowersInfo: BorrowersInfo;
  fxValues: FxValues;
  restrictions?: Restrictions;
  shortName?: string;
  creditedInitialCosts?: CreditedInitialCosts;
  lazy?: any;
  changingMargin?: {
    initialPeriod?: number;
    initialValue?: number;
    eventualValue?: number;
    eventualValueStatic?: boolean;
    initialValueStatic?: boolean;
    eventualInterestRateSum?: number;
    initialInterestRateSum?: number;
  },
  hasBrokerCommission?: boolean;
  debtToIncome?: DebtToIncome;
  homeStartProgram?: HomeStartProgram
}

export interface Provision {
  percentage: number;
  cost: number;
  description?: string;
  minPercentage?: number;
  maxPercentage?: number;
}

export interface DemandParameters {
  maxBorrowerAge: number;
  maiIncomeId: number;
  mainProfessionId: number;
  borrowerNumber: number;
}

export class SimulationDTO<T extends SimulationDemand> {
  userInput!: T;
  summary!: Summary;
  firstPeriodLength?: number;
  offers: Offer[] = [];
  refusedOffers?: Offer[];
  refusedLoans?: any[];
  demandParameters?: DemandParameters;
}

export interface SimulationPostDTO {
  id: string;
  simulation: Simulation;
  simulationUrl: string;
  summaryUrl: string;
  updateUrl: string;
}

export class Simulation<T extends MortgageSimulationDemand = MortgageSimulationDemand> extends SimulationDTO<T> {
  constructor(dto?: SimulationDTO<T>) {
    super();
    if (dto) {
      Object.assign(this, dto);
    }
  }

  get LTV(): Nullable<number> {
    return !!this.userInput.loanAmount && !!this.userInput.hypothecValue
      ? Math.round(this.userInput.loanAmount * 10000 / this.userInput.hypothecValue) / 100 : null;
  }

  get availableOferts(): number {
    return this.summary.availableOffersCount!;
  }

  get hypothecValue(): Nullable<number> {
    return this.userInput.hypothecValue || null;
  }

  get creditAmount(): number {
    return this.userInput.loanAmount!;
  }

  get loanPeriod(): number {
    return this.userInput.loanPeriod!;
  }

  get initialCosts(): Range {
    return this.summary.initialCostRange;
  }

  get initialCostsText(): string {
    return `${this.initialCosts.from} - ${this.initialCosts.until} ${EnvironmentService.Environment.currentCurrency()}`;
  }

  get firstInstallment(): Range {
    return this.summary.firstInstallmentRange;
  }

  get firstInstallmentText(): string {
    return `${this.firstInstallment.from} - ${this.firstInstallment.until} ${EnvironmentService.Environment.currentCurrency()}`;
  }

  get lastInstallment(): Range {
    return this.summary.lastInstallmentRange;
  }

  get lastInstallmentText(): string {
    return `${this.lastInstallment.from} - ${this.lastInstallment.until} ${EnvironmentService.Environment.currentCurrency()}`;
  }

  get fixedInstalment(): boolean {
    return this.userInput.fixedInstallments;
  }

  get maxWorthiness() {
    let max = 0;
    this.offers
      .filter(offer => offer.creditWorthiness)
      .forEach(offer => {
        if (offer.creditWorthiness!.maxCreditAmount > max) {
          max = offer.creditWorthiness!.maxCreditAmount;
        }
      });
    return max;
  }

  getClientByPesel(pesel: string): FFBorrower | undefined {
    let clientPeselYear = pesel.substr(0, 2);
    const clientPeselMonth = pesel.substr(2, 2);
    const prefixData = parseInt(clientPeselYear) < 30 ? '20' : '19';
    clientPeselYear = prefixData + clientPeselYear;
    const clientGender = parseInt(pesel[9]) % 2 === 0 ? Gender.FEMALE : Gender.MALE;
    if (this.userInput.households) {
      const borrowerArray = this.userInput.households.flatMap(households => households.borrowers);

      return borrowerArray.find(borrower => {
        const dateArray = borrower.birthDate.split('-');
        return parseInt(dateArray[0]) === parseInt(clientPeselMonth) &&
          parseInt(dateArray[1]) === parseInt(clientPeselYear) &&
          borrower.gender === clientGender;
      });
    }
    ;
    return undefined;
  }
}
